/**
 * @file Components/Tours/CustomTripWizard.jsx
 * @description Multi-step wizard for creating custom trip requests.
 * Steps: Interests → Details → Contact → Review & Submit.
 * Submits as a 'custom_trip' query to the CRM system.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronRight, ChevronLeft, Mountain, GraduationCap, Crown, Heart,
  Briefcase, Compass, Users, Calendar, DollarSign, MapPin, Mail, Phone,
  User, MessageSquare, Loader2, CheckCircle, Sparkles
} from 'lucide-react';
import { toast } from 'react-toastify';
import queryService from '../../services/queryService';

const interests = [
  { key: 'adventure', label: 'Adventure', icon: Mountain, color: 'from-orange-400 to-red-500' },
  { key: 'cultural', label: 'Cultural', icon: Compass, color: 'from-indigo-400 to-purple-500' },
  { key: 'educational', label: 'Educational', icon: GraduationCap, color: 'from-green-400 to-teal-500' },
  { key: 'luxury', label: 'Luxury', icon: Crown, color: 'from-amber-400 to-yellow-500' },
  { key: 'family', label: 'Family Fun', icon: Heart, color: 'from-pink-400 to-rose-500' },
  { key: 'corporate', label: 'Corporate', icon: Briefcase, color: 'from-slate-400 to-gray-600' },
  { key: 'wildlife', label: 'Wildlife', icon: Compass, color: 'from-lime-400 to-green-600' },
  { key: 'spiritual', label: 'Spiritual', icon: Sparkles, color: 'from-violet-400 to-purple-600' },
];

const budgetRanges = [
  { label: 'Under $500', value: 500 },
  { label: '$500 – $1,000', value: 1000 },
  { label: '$1,000 – $2,500', value: 2500 },
  { label: '$2,500 – $5,000', value: 5000 },
  { label: '$5,000+', value: 10000 },
];

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 200 : -200, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -200 : 200, opacity: 0 }),
};

const CustomTripWizard = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    selectedInterests: [],
    destination: '',
    groupSize: 2,
    duration: 5,
    budget: 2500,
    preferredDates: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    message: '',
  });

  const totalSteps = 4;

  const goNext = () => { setDirection(1); setStep(s => Math.min(s + 1, totalSteps - 1)); };
  const goBack = () => { setDirection(-1); setStep(s => Math.max(s - 1, 0)); };

  const toggleInterest = (key) => {
    setForm(f => ({
      ...f,
      selectedInterests: f.selectedInterests.includes(key)
        ? f.selectedInterests.filter(k => k !== key)
        : [...f.selectedInterests, key],
    }));
  };

  const canProceed = () => {
    if (step === 0) return form.selectedInterests.length > 0;
    if (step === 1) return form.destination.trim().length > 0;
    if (step === 2) return form.customerName.trim() && form.customerEmail.trim();
    return true;
  };

  const handleSubmit = async () => {
    if (!form.customerName.trim() || !form.customerEmail.trim()) {
      toast.error('Please fill in your name and email');
      return;
    }
    setSubmitting(true);
    try {
      await queryService.createQuery({
        type: 'custom_trip',
        customerName: form.customerName.trim(),
        customerEmail: form.customerEmail.trim(),
        customerPhone: form.customerPhone.trim(),
        subject: `Custom Trip: ${form.destination || 'Flexible'} (${form.groupSize} travelers)`,
        content: form.message || `I'm looking for a custom ${form.duration}-day trip to ${form.destination || 'a destination of your recommendation'} for ${form.groupSize} travelers.`,
        customTrip: {
          interests: form.selectedInterests,
          budget: form.budget,
          groupSize: form.groupSize,
          duration: form.duration,
          destination: form.destination,
          preferredDates: form.preferredDates,
        },
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err?.message || 'Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#243185] to-[#1a2460] p-5 flex items-center justify-between flex-shrink-0">
            <div>
              <h3 className="text-lg font-bold text-white">Design Your Dream Trip</h3>
              <p className="text-white/60 text-sm">Step {step + 1} of {totalSteps}</p>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white p-1"><X size={20} /></button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-gray-100 flex-shrink-0">
            <motion.div
              className="h-full bg-gradient-to-r from-[#243185] to-indigo-500"
              animate={{ width: `${((step + 1) / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {success ? (
            <div className="p-10 text-center flex-1">
              <CheckCircle size={56} className="text-green-500 mx-auto mb-4" />
              <h4 className="text-2xl font-bold text-gray-900 mb-2">Request Submitted!</h4>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                Our travel experts will craft a personalized itinerary and get back to you within 24 hours.
              </p>
              <button onClick={onClose} className="px-6 py-3 bg-[#243185] text-white rounded-xl font-semibold hover:bg-[#1a2460]">
                Close
              </button>
            </div>
          ) : (
            <>
              {/* Step content */}
              <div className="flex-1 overflow-y-auto p-6 min-h-[300px]">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={step}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.25 }}
                  >
                    {/* Step 0: Interests */}
                    {step === 0 && (
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 mb-1">What excites you?</h4>
                        <p className="text-gray-500 text-sm mb-5">Select one or more travel styles</p>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          {interests.map(({ key, label, icon: Icon, color }) => {
                            const selected = form.selectedInterests.includes(key);
                            return (
                              <button
                                key={key}
                                onClick={() => toggleInterest(key)}
                                className={`relative flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                                  selected
                                    ? 'border-[#243185] bg-indigo-50 shadow-md'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${color} flex items-center justify-center mb-2`}>
                                  <Icon size={18} className="text-white" />
                                </div>
                                <span className="text-xs font-semibold text-gray-700">{label}</span>
                                {selected && (
                                  <div className="absolute top-2 right-2 w-4 h-4 bg-[#243185] rounded-full flex items-center justify-center">
                                    <CheckCircle size={10} className="text-white" />
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Step 1: Trip Details */}
                    {step === 1 && (
                      <div className="space-y-5">
                        <h4 className="text-xl font-bold text-gray-900 mb-1">Trip Details</h4>
                        <p className="text-gray-500 text-sm mb-4">Tell us about your ideal trip</p>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Destination / Region *</label>
                          <div className="relative">
                            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              placeholder="e.g. Rajasthan, Kerala, Himalayas..."
                              value={form.destination}
                              onChange={(e) => setForm(f => ({ ...f, destination: e.target.value }))}
                              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Travelers</label>
                            <div className="relative">
                              <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="number" min={1} max={100}
                                value={form.groupSize}
                                onChange={(e) => setForm(f => ({ ...f, groupSize: parseInt(e.target.value) || 1 }))}
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (days)</label>
                            <div className="relative">
                              <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input
                                type="number" min={1} max={60}
                                value={form.duration}
                                onChange={(e) => setForm(f => ({ ...f, duration: parseInt(e.target.value) || 1 }))}
                                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none"
                              />
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Budget (per person)</label>
                          <div className="flex flex-wrap gap-2">
                            {budgetRanges.map(b => (
                              <button
                                key={b.value}
                                type="button"
                                onClick={() => setForm(f => ({ ...f, budget: b.value }))}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                                  form.budget === b.value
                                    ? 'bg-[#243185] text-white border-[#243185]'
                                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                }`}
                              >
                                {b.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Travel Dates</label>
                          <input
                            type="text"
                            placeholder="e.g. March 2026, Flexible..."
                            value={form.preferredDates}
                            onChange={(e) => setForm(f => ({ ...f, preferredDates: e.target.value }))}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none"
                          />
                        </div>
                      </div>
                    )}

                    {/* Step 2: Contact Info */}
                    {step === 2 && (
                      <div className="space-y-4">
                        <h4 className="text-xl font-bold text-gray-900 mb-1">Your Details</h4>
                        <p className="text-gray-500 text-sm mb-4">So our experts can reach you</p>

                        <div className="relative">
                          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text" placeholder="Full Name *"
                            value={form.customerName}
                            onChange={(e) => setForm(f => ({ ...f, customerName: e.target.value }))}
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                            required
                          />
                        </div>
                        <div className="relative">
                          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="email" placeholder="Email Address *"
                            value={form.customerEmail}
                            onChange={(e) => setForm(f => ({ ...f, customerEmail: e.target.value }))}
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none"
                            required
                          />
                        </div>
                        <div className="relative">
                          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="tel" placeholder="Phone (optional)"
                            value={form.customerPhone}
                            onChange={(e) => setForm(f => ({ ...f, customerPhone: e.target.value }))}
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none"
                          />
                        </div>
                        <div className="relative">
                          <MessageSquare size={16} className="absolute left-3 top-3 text-gray-400" />
                          <textarea
                            placeholder="Any special requirements or preferences..."
                            value={form.message}
                            onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                            rows={3}
                            className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm outline-none resize-none"
                          />
                        </div>
                      </div>
                    )}

                    {/* Step 3: Review */}
                    {step === 3 && (
                      <div>
                        <h4 className="text-xl font-bold text-gray-900 mb-1">Review Your Request</h4>
                        <p className="text-gray-500 text-sm mb-5">Make sure everything looks good</p>

                        <div className="space-y-3">
                          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Interests</span>
                              <span className="font-medium text-gray-900">{form.selectedInterests.map(i => i.charAt(0).toUpperCase() + i.slice(1)).join(', ')}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Destination</span>
                              <span className="font-medium text-gray-900">{form.destination || 'Flexible'}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Travelers</span>
                              <span className="font-medium text-gray-900">{form.groupSize}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Duration</span>
                              <span className="font-medium text-gray-900">{form.duration} days</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Budget</span>
                              <span className="font-medium text-gray-900">${form.budget.toLocaleString()}/person</span>
                            </div>
                            {form.preferredDates && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Dates</span>
                                <span className="font-medium text-gray-900">{form.preferredDates}</span>
                              </div>
                            )}
                          </div>
                          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Name</span>
                              <span className="font-medium text-gray-900">{form.customerName}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-500">Email</span>
                              <span className="font-medium text-gray-900">{form.customerEmail}</span>
                            </div>
                            {form.customerPhone && (
                              <div className="flex justify-between text-sm">
                                <span className="text-gray-500">Phone</span>
                                <span className="font-medium text-gray-900">{form.customerPhone}</span>
                              </div>
                            )}
                          </div>
                          {form.message && (
                            <div className="bg-gray-50 rounded-xl p-4">
                              <p className="text-sm text-gray-600">{form.message}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Footer buttons */}
              <div className="border-t border-gray-100 p-4 flex items-center justify-between flex-shrink-0">
                <button
                  onClick={step === 0 ? onClose : goBack}
                  className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 flex items-center gap-1"
                >
                  <ChevronLeft size={16} /> {step === 0 ? 'Cancel' : 'Back'}
                </button>
                {step < totalSteps - 1 ? (
                  <button
                    onClick={goNext}
                    disabled={!canProceed()}
                    className="px-6 py-2.5 bg-[#243185] text-white rounded-xl font-semibold text-sm hover:bg-[#1a2460] disabled:opacity-40 flex items-center gap-1 transition-all"
                  >
                    Next <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-6 py-2.5 bg-[#243185] text-white rounded-xl font-semibold text-sm hover:bg-[#1a2460] disabled:opacity-50 flex items-center gap-2 transition-all"
                  >
                    {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting...</> : 'Submit Request'}
                  </button>
                )}
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CustomTripWizard;
